import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Input,
  Select,
  Empty,
  Spin,
  Card,
  Tag,
  Button,
  Breadcrumb,
  message,
  Modal,
  Image,
  Divider,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  AppstoreOutlined,
  BarsOutlined,
  EyeOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import {
  productService,
  ProductItem,
  ProductListResponse,
} from '../../services/product.service';
import { categoryService, Category } from '../../services/category.service';
import { useCart } from '../../context/CartContext';
import ProductCard from '../../components/products/ProductCard';
import { Product } from '../../types/product';
import { showSuccess } from '../../utils/notificationUtils';
import {
  DEFAULT_PRODUCT_IMAGE,
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';

const { Option } = Select;
const { Meta } = Card;

const AllProducts: React.FC = () => {
  // URL search params
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(
    Number(searchParams.get('page')) || 1
  );
  const [pageSize] = useState<number>(Number(searchParams.get('limit')) || 12);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>(
    searchParams.get('search') || ''
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(
    searchParams.get('search') || ''
  );
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get('category_id') || ''
  );
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [categories, setCategories] = useState<Category[]>([]);
  const [quickViewProduct, setQuickViewProduct] = useState<ProductItem | null>(
    null
  );
  const [quickViewVisible, setQuickViewVisible] = useState(false);

  // Reference to search timer
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Ref for intersection observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const cart = useCart();

  // Fetch categories from backend API
  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const categoriesData = await categoryService.getPublicCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        message.error('Không thể tải danh mục sản phẩm');
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Define fetchProducts with support for pagination and appending
  const fetchProducts = useCallback(
    async (page: number, append: boolean = false) => {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const params = {
          page,
          limit: pageSize,
          search: debouncedSearchQuery || undefined,
          category_id: selectedCategory || undefined,
        };

        const response: ProductListResponse =
          await productService.getAllProducts(params);

        if (append) {
          setProducts((prev) => [...prev, ...response.products]);
        } else {
          setProducts(response.products);
        }

        setTotal(response.total);
        // Update hasMore based on current page and total pages
        setHasMore(page < response.totalPages);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        message.error('Không thể tải sản phẩm');
      } finally {
        setLoading(false);
        setLoadingMore(false);
        setIsSearching(false);
      }
    },
    [debouncedSearchQuery, pageSize, selectedCategory]
  );

  // Initial load and when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, false);
  }, [debouncedSearchQuery, selectedCategory, fetchProducts]);

  // Load more products when scrolling
  const loadMoreProducts = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProducts(nextPage, true);
  }, [currentPage, fetchProducts, hasMore, loadingMore]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px', // Add some margin to trigger earlier
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        hasMore &&
        !loadingMore &&
        !loading &&
        !isSearching
      ) {
        loadMoreProducts();
      }
    }, options);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMoreProducts, loadingMore, loading, isSearching]);

  // Update URL when filters change
  useEffect(() => {
    const params: Record<string, string> = {};

    if (debouncedSearchQuery) params.search = debouncedSearchQuery;
    if (selectedCategory) params.category_id = selectedCategory;

    setSearchParams(params);
  }, [debouncedSearchQuery, selectedCategory, setSearchParams]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsSearching(true);

    // Clear any existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    // Set a new timer for 1 second
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value);
      setIsSearching(false);
    }, 1000);
  };

  // Clean up timer on component unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1); // Reset to first page when changing category
  };

  const handleAddToCart = (product: ProductItem) => {
    cart.addItem(
      {
        id: product.id,
        productId: product.id,
        name: product.name,
        price: product.current_price,
        originalPrice: product.original_price,
        image: getProductImageUrl(product.images?.[0]),
        quantity: 1,
        weight: product.unit || '',
        unit: product.unit,
        stock: 10, // Placeholder, would come from actual API
        type: 'single',
      },
      1
    );

    // Show success notification
    showSuccess(
      'Đã thêm vào giỏ hàng',
      `${product.name} đã được thêm vào giỏ hàng của bạn.`
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const openQuickView = (product: ProductItem) => {
    setQuickViewProduct(product);
    setQuickViewVisible(true);
  };

  const closeQuickView = () => {
    setQuickViewVisible(false);
  };

  const addToCartFromQuickView = () => {
    if (quickViewProduct) {
      handleAddToCart(quickViewProduct);
      closeQuickView();
    }
  };

  // Convert ProductItem to Product for ProductCard component
  const mapToProduct = (product: ProductItem): Product => {
    return {
      id: product.id,
      name: product.name,
      description: product.description,
      images: product.images,
      unit: product.unit,
      original_price: product.original_price,
      is_on_sale: product.is_on_sale,
      is_trending: product.is_trending,
      status: 'active',
    };
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      {/* Breadcrumb */}
      <Breadcrumb
        className='p-3 bg-white shadow-sm rounded-lg border border-gray-100 mb-4'
        separator={<span className='text-gray-400 mx-1'>/</span>}
        items={[
          {
            title: (
              <Link
                to='/'
                className='flex items-center text-gray-600 hover:text-primary transition-colors'
              >
                <HomeOutlined className='mr-1' />
                <span>Trang chủ</span>
              </Link>
            ),
          },
          {
            title: (
              <span className='font-medium text-primary'>Tất cả sản phẩm</span>
            ),
          },
        ]}
      />

      <div className='flex flex-col md:flex-row gap-6'>
        {/* Filters sidebar */}
        <div className='w-full md:w-64 mb-4 md:mb-0'>
          <Card title='Bộ lọc' className='mb-4 sticky top-24'>
            <div className='mb-4'>
              <p className='font-medium mb-2'>Tìm kiếm</p>
              <Input
                placeholder='Tìm sản phẩm...'
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={handleSearchChange}
                suffix={isSearching ? <Spin size='small' /> : null}
                allowClear
              />
              {isSearching && (
                <div className='text-xs text-gray-500 mt-1'>
                  Đang tìm kiếm...
                </div>
              )}
            </div>

            <div className='mb-4'>
              <p className='font-medium mb-2'>Danh mục</p>
              <Select
                placeholder='Chọn danh mục'
                style={{ width: '100%' }}
                value={selectedCategory || undefined}
                onChange={handleCategoryChange}
                loading={categoriesLoading}
                allowClear
              >
                <Option key='all' value=''>
                  Tất cả danh mục
                </Option>
                {categories.map((category) => (
                  <Option key={category._id} value={category._id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Card>
        </div>

        {/* Products grid */}
        <div className='flex-1'>
          {/* Results header */}
          <div className='flex justify-between items-center mb-4'>
            <h1 className='text-2xl font-bold text-primary relative inline-block'>
              Tất cả sản phẩm
              <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
            </h1>
            <div className='flex items-center space-x-2'>
              <span className='text-gray-500 text-sm hidden md:inline'>
                Hiển thị:
              </span>
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('grid')}
                size='small'
              />
              <Button
                type={viewMode === 'list' ? 'primary' : 'default'}
                icon={<BarsOutlined />}
                onClick={() => setViewMode('list')}
                size='small'
              />
            </div>
          </div>

          {/* Results stats */}
          <p className='text-gray-500 mb-4'>
            {loading && currentPage === 1
              ? 'Đang tải sản phẩm...'
              : `Hiển thị ${products.length} trong số ${total} sản phẩm`}
          </p>

          {/* Products display */}
          {loading && currentPage === 1 ? (
            <div className='flex justify-center py-12'>
              <Spin size='large' />
            </div>
          ) : products.length === 0 ? (
            <Empty description='Không tìm thấy sản phẩm' className='py-12' />
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className='grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-1 xs:gap-2 sm:gap-4 md:gap-6'>
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className='space-y-4'>
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className='group relative transition-all duration-300 border border-gray-100 hover:border-primary/30 rounded-lg hover:shadow-lg bg-white overflow-hidden'
                    >
                      <div className='flex flex-col md:flex-row'>
                        {/* Badges */}
                        <div className='absolute top-2 left-2 z-10 flex flex-col gap-1'>
                          {product.is_on_sale && (
                            <span className='bg-red-500 text-white text-xs font-bold px-2 py-1 rounded'>
                              SALE
                            </span>
                          )}
                        </div>

                        {/* Trending badge */}
                        {product.is_trending && (
                          <div className='absolute top-2 right-2 z-10'>
                            <span className='bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded'>
                              HOT
                            </span>
                          </div>
                        )}

                        {/* Product image */}
                        <div className='md:w-1/4 relative overflow-hidden'>
                          <Link to={`/products/${product.id}`}>
                            <div className='overflow-hidden h-[200px] flex items-center justify-center p-4'>
                              <img
                                alt={product.name}
                                src={getProductImageUrl(product.images?.[0])}
                                className='w-full h-full object-contain transition-transform duration-500 group-hover:scale-105'
                                onError={handleProductImageError}
                              />
                            </div>
                          </Link>
                        </div>

                        {/* Product details */}
                        <div className='md:w-3/4 p-4 flex flex-col'>
                          <Link to={`/products/${product.id}`}>
                            <h3 className='text-lg font-semibold group-hover:text-primary transition-colors'>
                              {product.name}
                            </h3>
                          </Link>

                          <p className='text-gray-600 text-sm line-clamp-2 my-2'>
                            {product.description}
                          </p>

                          <div className='mt-2 mb-3'>
                            <span className='text-red-500 font-semibold text-lg'>
                              {formatPrice(product.current_price)}
                            </span>
                            {product.is_on_sale &&
                              product.original_price >
                                product.current_price && (
                                <span className='text-gray-400 line-through text-sm ml-2'>
                                  {formatPrice(product.original_price)}
                                </span>
                              )}
                          </div>

                          <div className='mt-auto pt-3 flex space-x-2'>
                            <Button
                              type='primary'
                              icon={<ShoppingCartOutlined />}
                              onClick={() => handleAddToCart(product)}
                              className='transition-all hover:bg-primary/90 shadow'
                            >
                              Thêm vào giỏ
                            </Button>

                            <Tooltip title='Xem nhanh'>
                              <Button
                                icon={<EyeOutlined />}
                                onClick={() => openQuickView(product)}
                                className='hover:text-primary hover:border-primary'
                              />
                            </Tooltip>

                            <Tooltip title='Yêu thích'>
                              <Button
                                icon={<HeartOutlined />}
                                className='hover:text-primary hover:border-primary'
                              />
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Load more indicator */}
              <div ref={loadMoreRef} className='mt-8 py-4 text-center'>
                {loadingMore && (
                  <div className='flex justify-center'>
                    <Spin size='default' />
                    <span className='ml-2 text-gray-500'>
                      Đang tải thêm sản phẩm...
                    </span>
                  </div>
                )}
                {!hasMore && products.length > 0 && (
                  <p className='text-gray-500'>Đã hiển thị tất cả sản phẩm</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quick View Modal */}
      <Modal
        open={quickViewVisible}
        onCancel={closeQuickView}
        footer={null}
        width={800}
        title={null}
        centered
        className='quick-view-modal'
      >
        {quickViewProduct && (
          <div className='flex flex-col md:flex-row gap-6'>
            <div className='md:w-1/2'>
              <Image
                src={getProductImageUrl(quickViewProduct.images?.[0])}
                alt={quickViewProduct.name}
                className='object-contain'
                preview={false}
                fallback={DEFAULT_PRODUCT_IMAGE}
              />
            </div>
            <div className='md:w-1/2 flex flex-col'>
              <h2 className='text-xl font-semibold'>{quickViewProduct.name}</h2>

              <div className='mt-2 mb-4'>
                <span className='text-red-500 font-semibold text-xl'>
                  {formatPrice(quickViewProduct.current_price)}
                </span>
                {quickViewProduct.is_on_sale &&
                  quickViewProduct.original_price >
                    quickViewProduct.current_price && (
                    <span className='text-gray-400 line-through text-sm ml-2'>
                      {formatPrice(quickViewProduct.original_price)}
                    </span>
                  )}
              </div>

              <div className='mb-4'>
                <p className='text-gray-700 line-clamp-4'>
                  {quickViewProduct.description}
                </p>
              </div>

              <div className='mt-2 space-x-2'>
                {quickViewProduct.is_trending && (
                  <Tag color='orange'>Trending</Tag>
                )}
                {quickViewProduct.is_on_sale && <Tag color='red'>Sale</Tag>}
              </div>

              <Divider />

              <div className='mt-auto space-y-3'>
                <Button
                  type='primary'
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCartFromQuickView}
                  block
                  size='large'
                >
                  Thêm vào giỏ hàng
                </Button>

                <Link to={`/products/${quickViewProduct.id}`}>
                  <Button block size='large'>
                    Xem chi tiết
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AllProducts;
